/**
 * ComparisonColumn Component
 *
 * Single column showing all metrics for one subject.
 * Used in two-column comparison layout.
 *
 * Displays:
 * - Header (icon, name, count)
 * - Sections grouped by title (Vanity, Sentiment, Demographics, etc.)
 * - Metrics with winner/loser styling
 *
 * Usage:
 * ```tsx
 * <ComparisonColumn
 *   subject={subjectA}
 *   sections={sectionsA}
 *   variant="blue"
 * />
 * ```
 */

import { MetricRow } from './MetricRow'
import type { ComparisonMetric } from '@/types/comparison'

interface ColumnSection {
  id: string
  title: string
  metrics: ColumnMetric[]
}

interface ColumnMetric {
  label: string
  value: number | string | null | undefined
  isWinner?: boolean
  delta?: number
  format?: ComparisonMetric['format']
}

interface ComparisonColumnProps {
  /**
   * Subject metadata
   */
  subject: {
    name: string
    icon?: string
    count?: number
  }

  /**
   * Sections with metrics
   */
  sections: ColumnSection[]

  /**
   * Color variant (blue, purple, green, orange)
   */
  variant: 'blue' | 'purple' | 'green' | 'orange'

  /**
   * Optional className
   */
  className?: string
}

/**
 * Get label and colors for variant (A, B, C, D)
 */
function getVariantConfig(variant: 'blue' | 'purple' | 'green' | 'orange'): { label: string; badgeClass: string } {
  const config = {
    blue: { label: 'A', badgeClass: 'bg-blue-500/20 text-blue-500' },
    purple: { label: 'B', badgeClass: 'bg-purple-500/20 text-purple-500' },
    green: { label: 'C', badgeClass: 'bg-green-500/20 text-green-500' },
    orange: { label: 'D', badgeClass: 'bg-orange-500/20 text-orange-500' },
  }

  return config[variant]
}

/**
 * Format count in compact notation.
 */
function formatCount(count: number): string {
  const absValue = Math.abs(count)

  if (absValue >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`
  }
  if (absValue >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`
  }
  return String(count)
}

/**
 * Single comparison column showing all metrics for one subject.
 */
export function ComparisonColumn({
  subject,
  sections,
  variant,
  className = '',
}: ComparisonColumnProps) {
  const { label: variantLabel, badgeClass } = getVariantConfig(variant)

  return (
    <div className={`bg-v11-surface border border-v11-border rounded-lg p-4 ${className}`}>
      {/* Header */}
      <div className="pb-4 border-b border-v11-border mb-4">
        <div className="flex items-center gap-2">
          {/* Variant Badge (A, B, C, D) */}
          <div className={`flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${badgeClass}`}>
            {variantLabel}
          </div>

          {/* Name */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold truncate text-v11-text-primary">
              {subject.name}
            </h3>
          </div>

          {/* Count */}
          {subject.count !== undefined && (
            <span className="text-xs text-v11-text-muted flex-shrink-0">
              {formatCount(subject.count)}
            </span>
          )}
        </div>
      </div>

      {/* Content - Sections */}
      <div className="space-y-5">
        {sections.map((section) => (
          <div key={section.id}>
            {/* Section Title */}
            <h4 className="text-xs uppercase tracking-wide text-v11-text-muted font-semibold mb-2">
              {section.title}
            </h4>

            {/* Metrics */}
            <div className="space-y-0">
              {section.metrics.map((metric, index) => (
                <MetricRow
                  key={`${section.id}-${metric.label}-${index}`}
                  label={metric.label}
                  value={metric.value}
                  isWinner={metric.isWinner}
                  delta={metric.delta}
                  format={metric.format}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Type exports
export type { ColumnSection, ColumnMetric }

// Default export
export default ComparisonColumn
